export type DeleteAction = 'local' | 'server' | null

interface ResolveDeleteActionParams {
  /** A deletion is already scheduled — show the pending banner, not a trash control. */
  isPendingDeletion: boolean
  /** Unsynced on-device draft (never reached the server). */
  isDraft: boolean
  /** Parent allows an immediate local delete (a local copy exists to remove). */
  canDelete: boolean
  /** This surface offers server-side soft-delete (the history page). */
  allowServerDelete: boolean
  /** The binder permits the owner to delete this submission. */
  deletionEnabled: boolean
}

/**
 * Decide what the trash control does for a row:
 *  - `null`   → no control (pending, or nothing deletable here)
 *  - `local`  → immediate on-device delete (drafts, recorder-list rows)
 *  - `server` → cancellable 3-day server soft-delete (synced + enabled on a
 *    surface that offers it)
 *
 * Server delete takes precedence over local for synced rows so a recording that
 * lives on the server is removed there, not just on this device.
 */
export function resolveDeleteAction({
  isPendingDeletion,
  isDraft,
  canDelete,
  allowServerDelete,
  deletionEnabled,
}: ResolveDeleteActionParams): DeleteAction {
  if (isPendingDeletion) return null
  if (isDraft) return canDelete ? 'local' : null
  if (allowServerDelete && deletionEnabled) return 'server'
  return canDelete ? 'local' : null
}
